<script setup>
  import { computed, onMounted, ref } from 'vue';
  import { useI18n } from 'vue-i18n';
  import CountryFlag from 'vue-country-flag-next';
  import axios from 'axios';

  const props = defineProps({
    hidden: {
      type: Boolean,
      default: false,
    },
  });
  const localeFlagMap = {
    en: 'gb',
    fr: 'fr',
    es: 'es',
    de: 'de',
    it: 'it',
    pt: 'pt',
    nl: 'nl',
    ru: 'ru',
    zh: 'cn',
    ja: 'jp',
    ko: 'kr',
    ar: 'sa',
    hi: 'in',
    tr: 'tr',
    pl: 'pl',
    sv: 'se',
    no: 'no',
    da: 'dk',
    fi: 'fi',
    el: 'gr',
  };

  const localeNames = {
    en: 'English',
    fr: 'Français',
    es: 'Español',
    de: 'Deutsch',
    it: 'Italiano',
    pt: 'Português',
    nl: 'Nederlands',
    ru: 'Русский',
    zh: '中文',
    ja: '日本語',
    ko: '한국어',
    ar: 'العربية',
    hi: 'हिन्दी',
    tr: 'Türkçe',
    pl: 'Polski',
    sv: 'Svenska',
    no: 'Norsk',
    da: 'Dansk',
    fi: 'Suomi',
    el: 'Ελληνικά',
  };

  const isOpen = ref(false);
  const { locale, messages } = useI18n();

  const available_locales = computed(() => [...new Set(usePage().props.available_locales)]);

  const fetchTranslations = async (lang) => {
    if (messages.value[lang]) return;
    try {
      const response = await axios.get(`/lang/${lang}.json`);
      messages.value[lang] = response.data;
      return nextTick();
    } catch (error) {
      console.error(`Failed to load language file for ${lang}`, error);
    }
  };

  const toggleDropdown = () => (isOpen.value = !isOpen.value);
  const selectLocale = async (newLocale) => {
    await fetchTranslations(newLocale);
    locale.value = newLocale;

    localStorage.setItem('selectedLocale', newLocale);
    isOpen.value = false;
    return nextTick();
  };
  const getFlag = (locale) => localeFlagMap[locale] || 'us';
  const selectedFlag = computed(() => getFlag(locale.value));
  const getLocaleName = (locale) => localeNames[locale] || locale;
  const container = ref(null);
  onMounted(async () => {
    // URL override wins over localStorage so a display PC can pin the
    // language via query string (e.g. /event/{token}?lang=de).
    const urlLocale = new URLSearchParams(window.location.search).get('lang');
    const storedLocale = localStorage.getItem('selectedLocale');
    if (urlLocale && available_locales.value.includes(urlLocale)) {
      selectLocale(urlLocale);
    } else if (storedLocale && available_locales.value.includes(storedLocale)) {
      selectLocale(storedLocale);
    } else {
      selectLocale('de');
    }
  });

  const handleClickOutside = (e) => {
    if (!container.value.contains(e.target)) {
      isOpen.value = false;
    }
  };

  onMounted(() => {
    window.addEventListener('click', handleClickOutside);
  });

  onBeforeUnmount(() => {
    window.removeEventListener('click', handleClickOutside);
  });
</script>

<template>
  <div
    :class="['relative inline-block', { hidden: props.hidden }]"
    ref="container"
  >
    <button
      @click="toggleDropdown"
      type="button"
      class="flex items-center gap-2 p-2 pt-0 border rounded-lg"
    >
      <country-flag :country="selectedFlag" size="normal" />
      <span class="mb-[-7px]">{{ getLocaleName(locale) }}</span>
    </button>
    <div
      v-if="isOpen"
      class="absolute mt-2 w-40 bg-white border rounded-lg shadow-lg"
    >
      <ul>
        <li v-for="locale in available_locales" :key="locale">
          <button
            @click="selectLocale(locale)"
            type="button"
            class="flex items-center w-full p-2 hover:bg-gray-200"
          >
            <country-flag :country="getFlag(locale)" size="normal" />
            <span class="ml-2">{{ getLocaleName(locale) }}</span>
          </button>
        </li>
      </ul>
    </div>
  </div>
</template>

<style>
  button:focus {
    outline: none;
  }
</style>
